import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import AppError from 'utils/appError';

// The payload of a File content block (ContentBlockKind.File): a file a Copilot tool made for the user, such as the
// Excel workbook of excel_workbook_create. The block's mimeType is the file's content type; the bytes are served by
// GET copilot/document/content/{sessionId}/{blockId} until the sweep clears the temp blob.
export interface CopilotFileBlockPayload {
  fileName: string;
  sizeBytes: number;
  // the session temp blob; absent once the sweep has cleared it, so the file has expired
  tempBlobName?: string;
  // set once the user saved the file to the library: the new library item and its category
  savedLinkId?: number;
  savedListId?: number;
  // the output location whose row is the default place to save the file (PLAN-069); absent: no default
  outputPurpose?: DocumentOutputPurpose;
  // a file a process staged and decides about itself (the supplier check's downloads and uploads): no Save to library
  managedByProcess?: boolean;
}

// The library item a Save to Library made of a File block.
export class CopilotFileLibrarySaveResult {
  linkId: number;

  listId: number;

  linkName: string;

  // the readable path of the folder the file went to; undefined when the server did not name it
  folderName?: string;

  fileName?: string;

  constructor() {
    this.linkId = 0;
    this.listId = 0;
    this.linkName = '';
  }
}

// Why the content endpoint did not serve a File block's bytes.
export enum CopilotFileUnavailableReason {
  // 410: the sweep cleared the temp blob
  Expired = 'expired',
  // 409: the malware scan has not answered yet
  ScanPending = 'scanPending',
  // 403: the malware scan found a threat
  ScanRefused = 'scanRefused',
}

// The reason behind a failed download, read from the AppError's HTTP status; undefined for any other failure.
export const getCopilotFileUnavailableReason = (err: unknown): CopilotFileUnavailableReason | undefined => {
  if (!(err instanceof AppError)) return undefined;

  switch (err.code) {
    case '410':
      return CopilotFileUnavailableReason.Expired;
    case '409':
      return CopilotFileUnavailableReason.ScanPending;
    case '403':
      return CopilotFileUnavailableReason.ScanRefused;
    default:
      return undefined;
  }
};

// The icon of a File block by its content type: the types a model or a tool may hand over (docx, xlsx, pptx, csv, png,
// jpeg, pdf).
export const fileBlockIconName = (mimeType: string | undefined): string => {
  switch ((mimeType ?? '').toLowerCase()) {
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'WordDocument';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'PowerPointDocument';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'text/csv':
      return 'ExcelDocument';
    case 'image/png':
    case 'image/jpeg':
      return 'FileImage';
    case 'application/pdf':
      return 'PDF';
    default:
      return 'Page';
  }
};

const isDocumentOutputPurpose = (value: unknown): value is DocumentOutputPurpose =>
  typeof value === 'number' && Object.values(DocumentOutputPurpose).includes(value);

export const parseFileBlockPayload = (payload: unknown): CopilotFileBlockPayload | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const file = payload as Partial<CopilotFileBlockPayload>;
  if (typeof file.fileName !== 'string' || file.fileName.trim() === '') return undefined;

  return {
    fileName: file.fileName,
    sizeBytes: typeof file.sizeBytes === 'number' ? file.sizeBytes : 0,
    tempBlobName: typeof file.tempBlobName === 'string' && file.tempBlobName !== '' ? file.tempBlobName : undefined,
    savedLinkId: typeof file.savedLinkId === 'number' ? file.savedLinkId : undefined,
    savedListId: typeof file.savedListId === 'number' ? file.savedListId : undefined,
    outputPurpose: isDocumentOutputPurpose(file.outputPurpose) ? file.outputPurpose : undefined,
    managedByProcess: file.managedByProcess === true,
  };
};
